import { Button } from '@suscri/ui';
import {
  User,
  Bell,
  Palette,
  Globe,
  CreditCard,
  Coins,
  Download,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { PushSettings } from '../components/PushSettings';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuthenticatedQuery } from '../hooks/useAuthenticatedQuery';
import { useAuthStore } from '../lib/auth';
import { dateLocale, setLocale as applyLocale } from '../lib/i18n';
import { applyTheme } from '../lib/theme';
import { trpc } from '../lib/trpc';

const THEMES = [
  { value: 'light', label: 'Claro' },
  { value: 'dark', label: 'Oscuro' },
  { value: 'system', label: 'Sistema' },
];

export function SettingsPage() {
  const { t } = useTranslation();
  const { user, updateUser, clearAuth } = useAuthStore();
  const { enabled } = useAuthenticatedQuery();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [deletePwd, setDeletePwd] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const utils = trpc.useUtils();
  const deleteAccount = trpc.auth.deleteAccount.useMutation({
    onSuccess: () => {
      clearAuth();
      navigate('/');
    },
    onError: e => setFeedback(e.message),
  });

  const exportData = async () => {
    setFeedback(null);
    const data = await utils.auth.exportData.fetch();
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suscri-datos-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Estado editable inicializado desde el usuario
  const [name, setName] = useState(user?.name ?? '');
  const [locale, setLocale] = useState(user?.locale ?? 'es');
  const [currency, setCurrency] = useState(user?.currency ?? 'EUR');
  const [theme, setTheme] = useState(user?.theme ?? 'system');

  const { data: billing } = trpc.billing.me.useQuery(undefined, { enabled });
  const updateProfile = trpc.auth.updateProfile.useMutation({
    onSuccess: data => {
      updateUser(data as never);
      setFeedback(t('Cambios guardados.'));
    },
    onError: () => setFeedback(t('No se pudieron guardar los cambios.')),
  });

  const save = (fields: Record<string, string>) => {
    setFeedback(null);
    updateProfile.mutate(fields);
  };

  const tabs = [
    { id: 'profile', name: t('Perfil'), icon: User },
    { id: 'preferences', name: t('Preferencias'), icon: Globe },
    { id: 'appearance', name: t('Apariencia'), icon: Palette },
    { id: 'notifications', name: t('Notificaciones'), icon: Bell },
    { id: 'billing', name: t('Facturación'), icon: CreditCard },
  ];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t('Cuenta')} title={t('Configuración')} description={t('Gestiona tu cuenta y preferencias')} />

      {feedback && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-center justify-between">
          <span>{feedback}</span>
          <button
            className="font-semibold text-blue-500 hover:text-blue-700"
            onClick={() => setFeedback(null)}
          >
            ✕
          </button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <div className="surface-card p-3 sm:p-4">
            <nav className="space-y-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left text-sm font-medium rounded-lg transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-blue-50 hover:shadow-sm'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="surface-card p-5 sm:p-6">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('Información del Perfil')}
                </h2>
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      {t('Nombre')}
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full rounded-lg border border-gray-300 bg-gray-100 px-4 py-3 text-gray-500"
                    />
                  </div>
                </div>
                <Button
                  onClick={() => save({ name })}
                  disabled={updateProfile.isPending}
                >
                  {updateProfile.isPending
                    ? t('Guardando…')
                    : t('Guardar cambios')}
                </Button>

                {/* Datos y privacidad (GDPR) */}
                <div className="mt-8 border-t border-gray-200 pt-6">
                  <h3 className="text-sm font-semibold uppercase text-gray-500">
                    {t('Datos y privacidad')}
                  </h3>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                    <Button
                      variant="outline"
                      onClick={exportData}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />{' '}
                      {t('Descargar mis datos')}
                    </Button>
                    {!confirmDelete ? (
                      <Button
                        variant="outline"
                        onClick={() => setConfirmDelete(true)}
                        className="flex items-center gap-2 border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" /> {t('Eliminar mi cuenta')}
                      </Button>
                    ) : null}
                  </div>

                  {confirmDelete && (
                    <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
                      <p className="text-sm font-medium text-red-800">
                        {t(
                          'Esta acción borra tu cuenta y todos tus datos de forma permanente. Escribe tu contraseña para confirmar.'
                        )}
                      </p>
                      <input
                        type="password"
                        placeholder={t('Tu contraseña')}
                        value={deletePwd}
                        onChange={e => setDeletePwd(e.target.value)}
                        className="mt-3 w-full max-w-xs rounded-lg border border-red-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
                      />
                      <div className="mt-3 flex gap-3">
                        <Button
                          onClick={() =>
                            deleteAccount.mutate({ password: deletePwd })
                          }
                          disabled={!deletePwd || deleteAccount.isPending}
                          className="bg-red-600 text-white hover:bg-red-700"
                        >
                          {deleteAccount.isPending
                            ? t('Eliminando…')
                            : t('Sí, eliminar mi cuenta')}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setConfirmDelete(false);
                            setDeletePwd('');
                          }}
                        >
                          {t('Cancelar')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('Preferencias')}
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('Idioma')}
                    </label>
                    <select
                      value={locale}
                      onChange={e => {
                        setLocale(e.target.value);
                        // Cambio inmediato del idioma de la interfaz (M7)
                        applyLocale(e.target.value === 'en' ? 'en' : 'es');
                      }}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="es">Español</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('Moneda')}
                    </label>
                    <select
                      value={currency}
                      onChange={e => setCurrency(e.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="EUR">EUR (€)</option>
                      <option value="USD">USD ($)</option>
                      <option value="GBP">GBP (£)</option>
                    </select>
                  </div>
                </div>
                <Button
                  onClick={() => save({ locale, currency })}
                  disabled={updateProfile.isPending}
                >
                  {updateProfile.isPending
                    ? t('Guardando…')
                    : t('Guardar preferencias')}
                </Button>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('Apariencia')}
                </h2>
                <div className="flex flex-wrap gap-3">
                  {THEMES.map(themeOption => (
                    <button
                      key={themeOption.value}
                      onClick={() => {
                        setTheme(themeOption.value);
                        applyTheme(themeOption.value);
                        save({ theme: themeOption.value });
                      }}
                      className={`rounded-lg border px-5 py-3 text-sm font-medium transition-all ${
                        theme === themeOption.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'
                      }`}
                    >
                      {t(themeOption.label)}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-500">
                  {t(
                    'El tema se aplica al instante y queda guardado en tu cuenta.'
                  )}
                </p>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('Notificaciones')}
                </h2>
                <p className="text-sm text-gray-600">
                  {t(
                    'Los avisos de renovación próxima y de presupuesto llegan siempre al centro de notificaciones de la app (la campana) y por email si el servicio de correo está configurado.'
                  )}
                </p>
                <div className="rounded-lg border border-gray-200 bg-white p-5">
                  <PushSettings />
                </div>
              </div>
            )}

            {activeTab === 'billing' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('Facturación')}
                </h2>
                <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow">
                    <CreditCard className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">
                      {t('Plan')} {billing?.plan?.name ?? 'Free'}
                      {billing?.plan?.isFounderPrice && (
                        <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">
                          {t('Precio fundador')}
                        </span>
                      )}
                    </p>
                    <p className="flex items-center gap-1 text-sm text-gray-600">
                      <Coins className="h-4 w-4 text-amber-500" />
                      {t('{{count}} créditos disponibles', {
                        count: billing?.credits ?? 0,
                      })}
                      {billing?.plan &&
                        ` · ${t('Renovación:')} ${new Date(billing.plan.currentPeriodEnd).toLocaleDateString(dateLocale())}`}
                    </p>
                  </div>
                  <Link to="/plan">
                    <Button variant="outline">{t('Gestionar plan')}</Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
